import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CloudSun, AlertTriangle, TrendingUp, GraduationCap, Store, ArrowRight, Map, Settings } from 'lucide-react';
import { LoadingState } from '../components/common/LoadingState';
import { getFavoritos } from '../services/storage';
import { Button } from '../components/ui/button';

// Colores personalizados para cada módulo
const modules = [
  {
    to: '/agroclima',
    icon: CloudSun,
    label: 'Agroclima',
    description: 'Estaciones',
    color: 'bg-[#506f5c]',
    testId: 'module-agroclima'
  },
  {
    to: '/avisos',
    icon: AlertTriangle,
    label: 'Avisos',
    description: 'Fitosanitarios',
    color: 'bg-[#7e834a]',
    testId: 'module-avisos'
  },
  {
    to: '/precios',
    icon: TrendingUp,
    label: 'Precios',
    description: 'Mercatenerife',
    color: 'bg-[#421927]',
    testId: 'module-precios'
  },
  {
    to: '/formacion',
    icon: GraduationCap,
    label: 'Formación',
    description: 'Formación agraria',
    color: 'bg-[#8b9058]',
    testId: 'module-formacion'
  },
  {
    to: '/comercios',
    icon: Store,
    label: 'Comercios',
    description: 'Agrícolas',
    color: 'bg-[#889988]',
    testId: 'module-comercios'
  },
  {
    to: '/mapa-cultivos',
    icon: Map,
    label: 'Cultivos',
    description: 'Mapa oficial',
    color: 'bg-[#a68081]',
    testId: 'module-mapa-cultivos'
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const [favoritos, setFavoritos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fav = getFavoritos();
    setFavoritos(fav);
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="px-4 py-4">
        <LoadingState message="Cargando..." />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 min-h-screen bg-[#fcf8f7]" data-testid="home-page" role="main">
      {/* Header con logo */}
      <header className="text-center mb-8">
        <div className="inline-block">
          <img 
            src="https://customer-assets.emergentagent.com/job_datacultivo/artifacts/pkxt3ok2_Logotipo%204.png" 
            alt="Datacultivo - Aplicación de datos agrícolas para Tenerife" 
            className="h-24 w-auto mx-auto"
          />
        </div>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto mt-1">
          Información para agricultores de Tenerife basada en datos abiertos
        </p>
      </header>

      {/* Módulos en grid de 3 columnas */}
      <nav aria-label="Módulos principales" className="grid grid-cols-3 gap-x-4 gap-y-6 max-w-md mx-auto">
        {modules.map(({ to, icon: Icon, label, description, color, testId }) => (
          <Link 
            key={to} 
            to={to} 
            data-testid={testId}
            className="flex flex-col items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg p-2"
            aria-label={`${label}: ${description}`}
          >
            <div 
              className={`${color} w-[77px] h-[77px] rounded-full flex items-center justify-center shadow-lg 
                         transform transition-all duration-200 group-hover:scale-110 group-active:scale-95
                         group-hover:shadow-xl`}
              aria-hidden="true"
            >
              <Icon className="h-9 w-9 text-[#fcf8f7]" strokeWidth={1.8} aria-hidden="true" />
            </div>
            <span className="mt-2 font-semibold text-foreground text-xs sm:text-sm text-center">{label}</span>
            <span className="text-xs text-muted-foreground text-center" aria-hidden="true">{description}</span>
          </Link>
        ))}
      </nav>

      {/* Línea separadora discreta */}
      <div className="max-w-md mx-auto mt-6 mb-2" role="separator" aria-hidden="true">
        <div className="border-t border-gray-300"></div>
      </div>

      {/* Botón Ajustes - icono rueda dentada */}
      <div className="flex justify-end max-w-md mx-auto">
        <Link 
          to="/mas" 
          data-testid="module-mas"
          className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Ajustes y configuración"
        >
          <Settings className="h-5 w-5" aria-hidden="true" />
        </Link>
      </div>

      {/* Info de favoritos si existen - CON ENLACES */}
      {(favoritos?.estacionNombre || favoritos?.productoFavorito || favoritos?.comarcaVina || favoritos?.comarcaPapa) && (
        <section aria-label="Tus favoritos" className="mt-8 bg-white rounded-xl p-4 border border-border shadow-sm max-w-md mx-auto">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Tus favoritos</h3>
          <div className="space-y-2" role="list">
            {favoritos?.estacionNombre && (
              <Button 
                variant="ghost" 
                className="w-full justify-between h-auto py-2 px-3 focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => navigate('/agroclima')}
                data-testid="goto-estacion-home"
                aria-label={`Ir a estación meteorológica ${favoritos.estacionNombre}`}
                role="listitem"
              >
                <span className="flex items-center gap-2 text-sm">
                  <CloudSun className="h-4 w-4 text-blue-500" aria-hidden="true" />
                  {favoritos.estacionNombre}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </Button>
            )}
            {favoritos?.comarcaVina && (
              <Button 
                variant="ghost" 
                className="w-full justify-between h-auto py-2 px-3 focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => navigate('/avisos')}
                data-testid="goto-comarca-vina-home"
                aria-label="Ir a avisos fitosanitarios de viña"
                role="listitem"
              >
                <span className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
                  Avisos viña
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </Button>
            )}
            {favoritos?.comarcaPapa && (
              <Button 
                variant="ghost" 
                className="w-full justify-between h-auto py-2 px-3 focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => navigate('/avisos')}
                data-testid="goto-comarca-papa-home"
                aria-label="Ir a avisos fitosanitarios de papa"
                role="listitem"
              >
                <span className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
                  Avisos papa
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </Button>
            )}
            {favoritos?.productoFavorito && (
              <Button 
                variant="ghost" 
                className="w-full justify-between h-auto py-2 px-3 focus-visible:ring-2 focus-visible:ring-primary"
                onClick={() => navigate('/precios')}
                data-testid="goto-producto-home"
                aria-label={`Ir a precios de ${favoritos.productoFavorito}`}
                role="listitem"
              >
                <span className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-emerald-500" aria-hidden="true" />
                  {favoritos.productoFavorito}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <footer className="mt-8 text-xs text-muted-foreground text-center px-4" role="contentinfo">
        Información orientativa. Consulte asesoramiento técnico para decisiones de cultivo.
      </footer>
    </div>
  );
}

export default HomePage;
